import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth, useGlobalVariables, useAccountUsage } from '../hooks';
import {
  ChangeEmailModal,
  ChangePasswordModal,
  DeleteAccountModal,
  NotificationPreferences,
} from '../components/account';
import { Loading } from '../components/common';
import { ROUTES } from '../routes';

export const Account: React.FC = () => {
  const { user, logout, deleteAccount, changePassword, changeEmail } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const navigate = useNavigate();
  const {
    variables,
    loading: gvLoading,
    fetchGlobalVariables,
    createGlobalVariable,
    updateGlobalVariable,
    deleteGlobalVariable,
  } = useGlobalVariables();
  const { usage, loading: usageLoading, error: usageError } = useAccountUsage();

  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const isPro = user?.tier === 'pro' || user?.tier === 'business';

  // Free users get the upgrade card instead, so there is nothing to fetch.
  useEffect(() => {
    if (isPro) {
      fetchGlobalVariables();
    }
  }, [isPro, fetchGlobalVariables]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setSaving(true);
    try {
      let parsedValue: unknown = newValue;
      try {
        parsedValue = JSON.parse(newValue);
      } catch {
        // keep as string
      }
      await createGlobalVariable(newKey.trim(), parsedValue);
      setNewKey('');
      setNewValue('');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    setSaving(true);
    try {
      let parsedValue: unknown = editValue;
      try {
        parsedValue = JSON.parse(editValue);
      } catch {
        // keep as string
      }
      await updateGlobalVariable(id, editKey.trim(), parsedValue);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (v: { id: string; key: string; value: unknown }) => {
    setEditingId(v.id);
    setEditKey(v.key);
    setEditValue(typeof v.value === 'string' ? v.value : JSON.stringify(v.value));
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.auth.login());
  };

  const handleReauthenticate = async () => {
    await logout();
    navigate(`${ROUTES.auth.login()}?redirect=${encodeURIComponent(ROUTES.account())}`);
  };

  const handlePasswordChanged = () => {
    setShowPasswordModal(false);
    toast.success('Password updated');
  };

  const handleAccountDeleted = () => {
    toast.success('Your account has been deleted');
    navigate(ROUTES.home());
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const tierLabel = (tier: string) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const tierBadgeClass = (tier: string) => {
    switch (tier) {
      case 'business':
        return 'badge-primary';
      case 'pro':
        return 'badge-secondary';
      default:
        return 'badge-ghost';
    }
  };

  return (
    <div className="flex-1 bg-base-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1>Account Settings</h1>

        {/* Profile Card */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Profile</h2>
            <div className="space-y-3 mt-2">
              <div className="flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <span className="text-sm text-base-content/70">Email</span>
                  <p className="text-sm font-medium break-all">{user?.email}</p>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setShowEmailModal(true)}>
                  Change email
                </button>
              </div>
              <div className="divider my-0" />
              <div className="flex justify-between items-center gap-4">
                <div>
                  <span className="text-sm text-base-content/70">Password</span>
                  {!user?.hasPassword && (
                    <p className="text-xs text-base-content/70">
                      Not set — you sign in with GitHub or a magic link.
                    </p>
                  )}
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setShowPasswordModal(true)}>
                  {user?.hasPassword ? 'Change password' : 'Set password'}
                </button>
              </div>
              <div className="divider my-0" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-base-content/70">Plan</span>
                <span className={`badge ${tierBadgeClass(user?.tier || 'free')}`}>
                  {tierLabel(user?.tier || 'free')}
                </span>
              </div>
              <div className="divider my-0" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-base-content/70">Member since</span>
                <span className="text-sm">{user?.createdAt ? formatDate(user.createdAt) : '—'}</span>
              </div>
            </div>
            {!isPro && (
              <div className="mt-4">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(ROUTES.pricing())}
                >
                  Upgrade to Pro
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Usage Overview Card */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Usage</h2>
            {usageLoading ? (
              <Loading className="py-4" />
            ) : usageError ? (
              <p className="text-sm text-error">Could not load usage data.</p>
            ) : usage ? (
              <div className="space-y-4 mt-2">
                {/* Projects */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Projects</span>
                    <span className="text-base-content/70">
                      {usage.projects} / {usage.limits.projects}
                    </span>
                  </div>
                  <progress
                    className={`progress w-full ${
                      usage.projects / usage.limits.projects >= 0.9
                        ? 'progress-error'
                        : usage.projects / usage.limits.projects >= 0.7
                          ? 'progress-warning'
                          : 'progress-primary'
                    }`}
                    value={usage.projects}
                    max={usage.limits.projects}
                  />
                </div>

                {/* Plan Limits Summary */}
                <div className="divider my-0" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-base-content/70">Endpoints per project</span>
                    <p className="text-sm font-medium">{usage.limits.endpointsPerProject}</p>
                  </div>
                  <div>
                    <span className="text-xs text-base-content/70">Requests per day</span>
                    <p className="text-sm font-medium">{usage.limits.requestsPerDay.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-xs text-base-content/70">Log retention</span>
                    <p className="text-sm font-medium">{usage.limits.logRetentionDays} day{usage.limits.logRetentionDays !== 1 ? 's' : ''}</p>
                  </div>
                  <div>
                    <span className="text-xs text-base-content/70">Global variables</span>
                    <p className="text-sm font-medium">{usage.limits.globalVariables}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Global Variables Card */}
        {isPro ? (
          <div className="card bg-base-100">
            <div className="card-body">
              <h2 className="card-title text-lg">Global Variables</h2>
              <p className="text-sm text-base-content/70 mb-4">
                Variables accessible from any endpoint via <code className="bg-base-300 px-1 rounded-field">{'{{globals.name}}'}</code>. Endpoint-scoped variables take precedence.
              </p>

              {gvLoading ? (
                <Loading className="py-4" />
              ) : (
                <>
                  {variables.length > 0 && (
                    <div className="overflow-x-auto mb-4">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Value</th>
                            <th className="w-24"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {variables.map((v) => (
                            <tr key={v.id}>
                              {editingId === v.id ? (
                                <>
                                  <td>
                                    <input
                                      type="text"
                                      className="input input-sm w-full"
                                      value={editKey}
                                      onChange={(e) => setEditKey(e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="input input-sm w-full"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <div className="flex gap-1">
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => handleUpdate(v.id)}
                                        disabled={saving}
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => setEditingId(null)}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="font-mono text-sm">{v.key}</td>
                                  <td className="text-sm max-w-xs truncate" title={typeof v.value === 'string' ? v.value : JSON.stringify(v.value)}>
                                    {typeof v.value === 'string' ? v.value : JSON.stringify(v.value)}
                                  </td>
                                  <td>
                                    <div className="flex gap-1">
                                      <button
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => startEditing(v)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="btn btn-ghost btn-xs text-error"
                                        onClick={() => deleteGlobalVariable(v.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {variables.length === 0 && (
                    <p className="text-sm text-base-content/70 mb-4">No global variables yet.</p>
                  )}

                  <form onSubmit={handleCreate} className="flex gap-2 items-end">
                    <div className="fieldset flex-1">
                      <label className="fieldset-legend">
                        Key
                      </label>
                      <input
                        type="text"
                        className="input input-sm"
                        placeholder="e.g. api_base_url"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        required
                      />
                    </div>
                    <div className="fieldset flex-1">
                      <label className="fieldset-legend">
                        Value
                      </label>
                      <input
                        type="text"
                        className="input input-sm"
                        placeholder="e.g. https://api.example.com"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={saving || !newKey.trim()}
                    >
                      Add Variable
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="card bg-base-100">
            <div className="card-body">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="card-title text-lg">
                    Global Variables
                    <span className="badge badge-secondary badge-sm">Pro</span>
                  </h2>
                  <p className="text-sm text-base-content/70 mt-2">
                    Define a value once on your account and use it from any endpoint in any project via <code className="bg-base-300 px-1 rounded-field">{'{{globals.name}}'}</code>. Project variables (<code className="bg-base-300 px-1 rounded-field">{'{{project.name}}'}</code>) are scoped to a single project.
                  </p>
                </div>
                <button
                  className="btn btn-primary btn-sm shrink-0"
                  onClick={() => navigate(ROUTES.pricing())}
                >
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        )}

        {user && <NotificationPreferences emailVerified={user.emailVerified} />}

        {/* Danger Zone */}
        <div className="card bg-base-100 border border-error/20">
          <div className="card-body">
            <h2 className="card-title text-lg text-error">Danger Zone</h2>
            <div className="flex justify-between items-center mt-2">
              <div>
                <p className="text-sm font-medium">Sign out</p>
                <p className="text-xs text-base-content/70">Sign out of your account on this device.</p>
              </div>
              <button className="btn btn-outline btn-error btn-sm" onClick={handleLogout}>
                Sign Out
              </button>
            </div>
            <div className="divider my-1" />
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">Delete account</p>
                <p className="text-xs text-base-content/70">
                  Permanently delete your account and your personal projects.
                </p>
              </div>
              <button className="btn btn-error btn-sm" onClick={() => setShowDeleteModal(true)}>
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {user && (
        <ChangePasswordModal
          isOpen={showPasswordModal}
          user={user}
          onSubmit={changePassword}
          onChanged={handlePasswordChanged}
          onReauthenticate={handleReauthenticate}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {user && (
        <ChangeEmailModal
          isOpen={showEmailModal}
          user={user}
          onSubmit={changeEmail}
          onReauthenticate={handleReauthenticate}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {user && (
        <DeleteAccountModal
          isOpen={showDeleteModal}
          user={user}
          onDelete={deleteAccount}
          onDeleted={handleAccountDeleted}
          onReauthenticate={handleReauthenticate}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};
